import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const NAV_LINKS = [
    { href: '#analyse', label: 'Real meals' },
    { href: '#how', label: 'How it works' },
    { href: '#features', label: 'Features' },
    { href: '#screens', label: 'Screens' },
    { href: '#about', label: 'About' },
    { href: '#faq', label: 'FAQ' },
];

const Header: React.FC = () => {
    const [navOpen, setNavOpen] = useState(false);

    // Close the mobile menu once the viewport is wide enough to show the full nav.
    useEffect(() => {
        const mq = window.matchMedia('(min-width: 1024px)');
        const onChange = (e: MediaQueryListEvent) => {
            if (e.matches) setNavOpen(false);
        };
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setNavOpen(false);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, []);

    // Stop the page scrolling underneath the open menu.
    useEffect(() => {
        if (!navOpen) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [navOpen]);

    return (
        <>
            <header className="pointer-events-none fixed inset-x-0 top-0 z-[70] flex justify-center px-4 py-3.5">
                <div className="pointer-events-auto flex h-[60px] w-full max-w-[1180px] items-center gap-[clamp(14px,2.2vw,30px)] rounded-full border border-dark-gunmetal bg-[rgba(27,32,38,0.74)] pl-5 pr-2 shadow-[0_12px_32px_rgba(0,0,0,0.4)] backdrop-blur-[20px]">
                    <a href="#top" aria-label="Fork home" className="flex shrink-0 items-center">
                        <img
                            src="/assets/fork-logo.png"
                            alt="FORK."
                            className="h-[22px] w-auto object-contain"
                        />
                    </a>

                    {/* desktop nav */}
                    <nav className="ml-auto hidden items-center gap-[clamp(12px,1.8vw,24px)] lg:flex">
                        {NAV_LINKS.map((link) => (
                            <a
                                key={link.href}
                                href={link.href}
                                className="text-[11px] font-medium uppercase tracking-[0.12em] text-dark-textSecondary transition-colors hover:text-white"
                            >
                                {link.label}
                            </a>
                        ))}
                    </nav>

                    <a
                        href="#get"
                        className="hidden h-11 items-center rounded-full bg-accent-bright px-[22px] text-sm font-bold uppercase tracking-[1.68px] text-white shadow-[0_8px_24px_rgba(3,147,231,0.35)] transition-[transform,background] hover:bg-accent active:scale-[0.97] lg:inline-flex"
                    >
                        Get the app
                    </a>

                    {/* mobile toggle */}
                    <button
                        onClick={() => setNavOpen((v) => !v)}
                        aria-label={navOpen ? 'Close menu' : 'Menu'}
                        aria-expanded={navOpen}
                        aria-controls="mobile-menu"
                        className="ml-auto inline-flex h-11 w-11 items-center justify-center rounded-full border border-dark-gunmetal bg-[#3F4346] text-white lg:hidden"
                    >
                        {navOpen ? <X className="h-[18px] w-[18px]" /> : <Menu className="h-[18px] w-[18px]" />}
                    </button>
                </div>
            </header>

            <AnimatePresence>
                {navOpen && (
                    <>
                        {/* Scrim: dims the page and gives tap-outside-to-close.
                            It also guarantees the menu is readable even where
                            backdrop-filter is unsupported on an animated node. */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.18, ease: 'easeOut' }}
                            onClick={() => setNavOpen(false)}
                            aria-hidden="true"
                            className="fixed inset-0 z-[68] bg-black/60 lg:hidden"
                        />

                        <motion.nav
                            id="mobile-menu"
                            aria-label="Main"
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.18, ease: 'easeOut' }}
                            className="fixed inset-x-4 top-[86px] z-[69] grid max-h-[calc(100vh-102px)] gap-0.5 overflow-y-auto rounded-[34px] border border-dark-gunmetal bg-dark-background p-3 shadow-[0_12px_32px_rgba(0,0,0,0.40)] lg:hidden"
                        >
                            {NAV_LINKS.map((link) => (
                                <a
                                    key={link.href}
                                    href={link.href}
                                    onClick={() => setNavOpen(false)}
                                    className="flex min-h-[48px] items-center rounded-xl px-4 text-[13px] font-semibold uppercase tracking-[0.12em] text-dark-textSecondary transition-colors hover:bg-[#3F4346] hover:text-white active:bg-[#3F4346] active:text-white"
                                >
                                    {link.label}
                                </a>
                            ))}
                            <a
                                href="#get"
                                onClick={() => setNavOpen(false)}
                                className="mt-1.5 flex h-12 items-center justify-center rounded-xl bg-accent-bright text-base font-bold uppercase tracking-[1.92px] text-white transition-colors hover:bg-accent"
                            >
                                Get the app
                            </a>
                        </motion.nav>
                    </>
                )}
            </AnimatePresence>
        </>
    );
};

export default Header;
